import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { passwordError } from "../lib/passwordPolicy";

export default function Home() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE = process.env.REACT_APP_API_URL || "";

  // A new Google sign-up that arrived without Terms acceptance is bounced back
  // here as /login?error=terms — switch to Register and prompt them to accept.
  // A Google sign-in/sign-up for an account still awaiting admin approval is
  // bounced back as /login?error=pending. A Google sign-in/sign-up outside the
  // current beta (public access closed) is bounced back as /login?error=invite_only.
  useEffect(() => {
    const err = new URLSearchParams(location.search).get("error");
    if (err === "terms") {
      setIsLogin(false);
      setError("Please accept the Terms of Service and Privacy Policy to create an account.");
    } else if (err === "pending") {
      setError("Your account is pending approval. We'll notify you once it's active.");
    } else if (err === "invite_only") {
      setError("Registration is currently invite-only. Request beta access from the homepage.");
    }
  }, [location.search]);

  // After auth, send disputants/witnesses straight to their disputes list;
  // everyone else lands on the new-dispute form. Falls back to the form on any
  // lookup error so login never breaks over this.
  const landingPathFor = async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/ai/disputes/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return "/submit-dispute";
      const data = await res.json();
      const isParticipant = (data.disputes || []).some(
        (d) => d.role === "disputant" || d.role === "witness"
      );
      return isParticipant ? "/all-disputes" : "/submit-dispute";
    } catch {
      return "/submit-dispute";
    }
  };

  // Registration (password or Google) requires accepting the Terms.
  const needsTerms = !isLogin && !agreedToTerms;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (needsTerms) {
      setError("Please accept the Terms of Service and Privacy Policy to register.");
      return;
    }
    if (!isLogin) {
      const pwError = passwordError(password);
      if (pwError) {
        setError(pwError);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }
    try {
      const data = isLogin
        ? await login(email, password)
        : await register(username, email, password, agreedToTerms);
      if (data?.token) {
        navigate(await landingPathFor(data.token));
      } else {
        // Registration sent a verification email instead of logging in
        // directly — nothing to navigate to yet.
        setMessage(data?.message || "Check your email to verify your account.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogleLogin = () => {
    if (needsTerms) {
      setError("Please accept the Terms of Service and Privacy Policy to register.");
      return;
    }
    // Signal acceptance to the backend so a NEW Google account can be created.
    // (Existing users signing in via the Login tab send no flag and are unaffected.)
    const qs = !isLogin && agreedToTerms ? "?terms=accepted" : "";
    window.location.href = `${API_BASE}/api/auth/google${qs}`;
  };

  return (
    <div style={styles.page}>
      <SiteHeader />
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>
            Welcome back
          </h1>
          <p style={styles.subtitle}>Sign in to your disputefAI account</p>

        <div style={styles.tabContainer}>
          <button
            style={{ ...styles.tab, borderBottom: isLogin ? "2px solid #0e2030" : "none", color: isLogin ? "#111" : "#888" }}
            onClick={() => setIsLogin(true)}
          >Login</button>
          <button
            style={{ ...styles.tab, borderBottom: !isLogin ? "2px solid #0e2030" : "none", color: !isLogin ? "#111" : "#888" }}
            onClick={() => setIsLogin(false)}
          >Register</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {!isLogin && (
            <input
              type="text"
              placeholder="Username"
              style={styles.input}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          )}
          <input
            type="email"
            placeholder="Email"
            style={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            style={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {!isLogin && (
            <>
              <input
                type="password"
                placeholder="Confirm password"
                style={styles.input}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <p style={styles.hint}>
                At least 8 characters, including an uppercase letter, a number, and a special character.
              </p>
            </>
          )}

          {!isLogin && (
            <label style={styles.termsRow}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.termsText}>
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" style={styles.link}>
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                  Privacy Policy
                </Link>.
              </span>
            </label>
          )}

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            disabled={needsTerms}
            style={{ ...styles.button, ...(needsTerms ? styles.buttonDisabled : {}) }}
          >
            {isLogin ? "Sign In" : "Sign Up"}
          </button>
        </form>

        {isLogin && (
          <div style={styles.forgotPass}>
            <Link to="/forgot-password" style={styles.link}>Forgot Password?</Link>
          </div>
        )}

        <div style={styles.divider}>
          <span style={styles.dividerText}>OR</span>
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={needsTerms}
          style={{ ...styles.googleButton, ...(needsTerms ? styles.buttonDisabled : {}) }}
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={styles.googleIcon} />
          Continue with Google
        </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  // Flex column so SiteFooter's `marginTop: auto` pins it to the bottom of the
  // viewport — same shell as pageStyles.root.
  page: {
    minHeight: "100vh",
    background: "#F4F4F0",
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  // `flex: 1` rather than a 100vh-minus-header guess: the old fixed height was
  // taller than the space left over, which pushed the footer below the fold and
  // added a scrollbar to a page that fits on one screen.
  container: {
    display: "flex", alignItems: "center", justifyContent: "center",
    flex: 1, padding: "32px 20px",
  },
  card: {
    background: "white", padding: "40px", borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.05)", width: "100%", maxWidth: "400px",
    textAlign: "center"
  },
  title: { fontSize: "24px", fontWeight: "700", marginBottom: "8px" },
  subtitle: { fontSize: "14px", color: "#666", marginBottom: "32px" },
  tabContainer: { display: "flex", marginBottom: "24px", borderBottom: "1px solid #eee" },
  tab: { flex: 1, padding: "12px", background: "none", border: "none", cursor: "pointer", fontWeight: "600", fontSize: "14px" },
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  input: {
    padding: "12px", borderRadius: "8px", border: "1px solid #ddd",
    fontSize: "14px", outline: "none", fontFamily: "inherit"
  },
  button: {
    padding: "12px", borderRadius: "8px", border: "none",
    background: "#0e2030", color: "white", fontWeight: "700",
    cursor: "pointer", fontSize: "14px", marginTop: "8px"
  },
  termsRow: {
    display: "flex", alignItems: "flex-start", gap: "8px",
    textAlign: "left", fontSize: "12.5px", color: "#555", cursor: "pointer",
  },
  checkbox: {
    marginTop: "2px", width: "16px", height: "16px",
    flexShrink: 0, cursor: "pointer", accentColor: "#0e2030",
  },
  termsText: { lineHeight: 1.5 },
  hint: { fontSize: "11.5px", color: "#888", margin: "-8px 0 0 0", textAlign: "left", lineHeight: 1.45 },
  buttonDisabled: { opacity: 0.5, cursor: "not-allowed" },
  error: { color: "#ff4d4d", fontSize: "12px", margin: "0" },
  success: { color: "#28a745", fontSize: "12px", margin: "0" },
  forgotPass: { marginTop: "16px", textAlign: "right" },
  link: { color: "#7c6af7", fontSize: "12px", textDecoration: "none" },
  divider: {
    margin: "24px 0", position: "relative", borderBottom: "1px solid #eee"
  },
  dividerText: {
    position: "absolute", top: "-10px", left: "50%", transform: "translateX(-50%)",
    background: "white", padding: "0 10px", fontSize: "12px", color: "#999"
  },
  googleButton: {
    width: "100%", padding: "12px", borderRadius: "8px",
    border: "1px solid #ddd", background: "white", color: "#444",
    fontWeight: "600", cursor: "pointer", fontSize: "14px",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
  },
  googleIcon: { width: "18px", height: "18px" }
};
